import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plus, Trash2, Type, Palette, AlignLeft, AlignCenter, AlignRight,
  Bold, Italic, ChevronUp, ChevronDown
} from 'lucide-react';

const COLORS = [
  '#ffffff', '#000000', '#ff0000', '#00ff00', '#0000ff', '#ffff00',
  '#ff00ff', '#00ffff', '#ff6600', '#9900ff', '#ff0066', '#bef264',
  '#ffa500', '#808080', '#c0c0c0', '#800000', '#008000', '#000080'
];

const FONTS = [
  { value: 'Oswald', label: 'Impact (Classic)' },
  { value: 'Inter', label: 'Inter (Modern)' },
  { value: 'Caveat', label: 'Handwritten' },
  { value: 'Comic Neue', label: 'Comic' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
];

const Toolbar = ({ 
  textLayers, 
  selectedLayer, 
  selectedLayerId,
  onSelectLayer, 
  onUpdateLayer,
  onAddLayer,
  onDeleteLayer 
}) => {

  const ColorPicker = ({ value, onChange, label }) => (
    <Popover>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 px-3 py-2 bg-[#18181b] border border-[#27272a] rounded-lg hover:border-[#3f3f46] transition-colors">
          <div 
            className="w-5 h-5 rounded border border-[#3f3f46]" 
            style={{ backgroundColor: value }}
          />
          <span className="text-sm text-[#a1a1aa]">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3 bg-[#18181b] border-[#27272a]" align="start">
        <div className="color-picker-grid">
          {COLORS.map((color) => (
            <button
              key={color}
              className={`color-swatch ${value === color ? 'active' : ''}`}
              style={{ backgroundColor: color }}
              onClick={() => onChange(color)}
            />
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-10 h-10 p-0 border-0 cursor-pointer"
          />
          <Input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="flex-1 bg-[#09090b] border-[#27272a] text-white text-sm"
            placeholder="#ffffff"
          />
        </div>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="toolbar-sidebar">
      <ScrollArea className="h-full pr-4">
        {/* Text Layers */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Text Layers</h3>
            <Button
              size="sm"
              onClick={onAddLayer}
              className="h-8 bg-[#bef264] text-black hover:bg-[#a3e635]"
              data-testid="add-text-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
          
          <div className="space-y-2">
            {textLayers.map((layer, index) => (
              <div
                key={layer.id}
                className={`text-layer-item ${layer.id === selectedLayerId ? 'active' : ''}`}
                onClick={() => onSelectLayer(layer.id)}
                data-testid={`layer-item-${layer.id}`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Type className="w-4 h-4 text-[#a1a1aa] flex-shrink-0" />
                  <span className="text-sm text-white truncate">
                    {layer.text || `Text ${index + 1}`}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Move up
                      const idx = textLayers.findIndex(l => l.id === layer.id);
                      if (idx > 0) {
                        const newLayers = [...textLayers];
                        [newLayers[idx], newLayers[idx-1]] = [newLayers[idx-1], newLayers[idx]];
                        // This would need to be lifted up to parent
                      }
                    }}
                    className="p-1 text-[#a1a1aa] hover:text-white"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteLayer(layer.id);
                    }}
                    className="p-1 text-[#a1a1aa] hover:text-red-400"
                    data-testid={`delete-layer-${layer.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedLayer && (
          <>
            <Separator className="bg-[#27272a] my-6" />

            {/* Text Content */}
            <div className="mb-6">
              <Label className="text-sm font-semibold text-white uppercase tracking-wider mb-3 block">
                Text Content
              </Label>
              <Input
                value={selectedLayer.text}
                onChange={(e) => onUpdateLayer(selectedLayerId, { text: e.target.value })}
                placeholder="Enter text..."
                className="bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#bef264]"
                data-testid="text-input"
              />
            </div>

            {/* Font Settings */}
            <div className="mb-6">
              <Label className="text-sm font-semibold text-white uppercase tracking-wider mb-3 block">
                Font
              </Label>
              <Select
                value={selectedLayer.fontFamily}
                onValueChange={(value) => onUpdateLayer(selectedLayerId, { fontFamily: value })}
              >
                <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white" data-testid="font-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  {FONTS.map((font) => (
                    <SelectItem key={font.value} value={font.value} className="text-white">
                      {font.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Font Size */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold text-white uppercase tracking-wider">
                  Size
                </Label>
                <span className="text-sm text-[#a1a1aa]">{selectedLayer.fontSize}px</span>
              </div>
              <Slider
                value={[selectedLayer.fontSize]}
                onValueChange={([value]) => onUpdateLayer(selectedLayerId, { fontSize: value })}
                min={12}
                max={100}
                step={1}
                className="py-2"
                data-testid="font-size-slider"
              />
            </div>

            {/* Text Style */}
            <div className="mb-6">
              <Label className="text-sm font-semibold text-white uppercase tracking-wider mb-3 block">
                Style
              </Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedLayer.bold ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onUpdateLayer(selectedLayerId, { bold: !selectedLayer.bold })}
                  className={selectedLayer.bold ? 'bg-[#bef264] text-black' : 'border-[#27272a] text-white'}
                  data-testid="bold-btn"
                >
                  <Bold className="w-4 h-4" />
                </Button>
                <Button
                  variant={selectedLayer.italic ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onUpdateLayer(selectedLayerId, { italic: !selectedLayer.italic })}
                  className={selectedLayer.italic ? 'bg-[#bef264] text-black' : 'border-[#27272a] text-white'}
                  data-testid="italic-btn"
                >
                  <Italic className="w-4 h-4" />
                </Button>
                <Button
                  variant={selectedLayer.uppercase ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onUpdateLayer(selectedLayerId, { uppercase: !selectedLayer.uppercase })}
                  className={selectedLayer.uppercase ? 'bg-[#bef264] text-black' : 'border-[#27272a] text-white'}
                  data-testid="uppercase-btn"
                >
                  AA
                </Button>
              </div>
            </div>

            {/* Text Alignment */}
            <div className="mb-6">
              <Label className="text-sm font-semibold text-white uppercase tracking-wider mb-3 block">
                Alignment
              </Label>
              <div className="flex gap-2">
                <Button
                  variant={selectedLayer.align === 'left' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onUpdateLayer(selectedLayerId, { align: 'left' })}
                  className={selectedLayer.align === 'left' ? 'bg-[#bef264] text-black' : 'border-[#27272a] text-white'}
                >
                  <AlignLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant={selectedLayer.align === 'center' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onUpdateLayer(selectedLayerId, { align: 'center' })}
                  className={selectedLayer.align === 'center' ? 'bg-[#bef264] text-black' : 'border-[#27272a] text-white'}
                >
                  <AlignCenter className="w-4 h-4" />
                </Button>
                <Button
                  variant={selectedLayer.align === 'right' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onUpdateLayer(selectedLayerId, { align: 'right' })}
                  className={selectedLayer.align === 'right' ? 'bg-[#bef264] text-black' : 'border-[#27272a] text-white'}
                >
                  <AlignRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Separator className="bg-[#27272a] my-6" />

            {/* Colors */}
            <div className="mb-6">
              <Label className="text-sm font-semibold text-white uppercase tracking-wider mb-3 block">
                Colors
              </Label>
              <div className="space-y-3">
                <ColorPicker
                  value={selectedLayer.color}
                  onChange={(color) => onUpdateLayer(selectedLayerId, { color })}
                  label="Text Color"
                />
                <ColorPicker
                  value={selectedLayer.strokeColor}
                  onChange={(color) => onUpdateLayer(selectedLayerId, { strokeColor: color })}
                  label="Stroke Color"
                />
              </div>
            </div>

            {/* Stroke Width */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold text-white uppercase tracking-wider">
                  Stroke
                </Label>
                <span className="text-sm text-[#a1a1aa]">{selectedLayer.strokeWidth}px</span>
              </div>
              <Slider
                value={[selectedLayer.strokeWidth]}
                onValueChange={([value]) => onUpdateLayer(selectedLayerId, { strokeWidth: value })}
                min={0}
                max={10}
                step={0.5}
                className="py-2"
                data-testid="stroke-slider"
              />
            </div>

            {/* Shadow */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold text-white uppercase tracking-wider">
                  Shadow
                </Label>
                <span className="text-sm text-[#a1a1aa]">{selectedLayer.shadowBlur}px</span>
              </div>
              <Slider
                value={[selectedLayer.shadowBlur]}
                onValueChange={([value]) => onUpdateLayer(selectedLayerId, { shadowBlur: value })}
                min={0}
                max={20}
                step={1}
                className="py-2"
                data-testid="shadow-slider"
              />
            </div>
          </>
        )}
      </ScrollArea>
    </div>
  );
};

export default Toolbar;
